/* ============================================================
   PINSEEKER · section and tap events (PS_V4 session 8, T2)

   Publishes custom events with Shopify's own analytics API
   (Shopify.analytics.publish) and sends each one to GA4 through
   the Google & YouTube channel's own tag on the page (window.gtag).
   That tag exists only once the visitor has accepted analytics, so
   an event shares the visit's client id and session and nothing is
   sent without consent. Events raised before the tag has loaded
   wait in a short queue (40 at most) and are dropped if it never
   loads. There is NO custom pixel: the one tried on 2 Oct 2026
   (docs/runbooks/ps-v4/custom-pixel.js, withdrawn) ran a second
   gtag.js in Shopify's sandbox, which replaced the visitor's _ga
   cookie and started a session of its own.

   Events (each once per page view, except taps):
     ps_section_view  a homepage or product page section is half on screen
                      { section, section_type, position, template }
     ps_hero_cta      a hero button or the bundle link is tapped  { which, href }
     ps_card_click    a product card is opened   { handle, list, list_type }
     ps_quick_add     quick add pressed (plus, or Choose)  { handle, kind, list, list_type }
     ps_popup         the welcome popup  { action: shown | closed | submitted }
     ps_sticky_atc    Add to bag on the sticky bar  { handle }
     ps_code_applied  a code applied or rejected in the bag  { result, where }
     ps_ugc_open      a UGC tile opened  { position, has_product, rail }
   Every event also carries { template }.

   No personal data, ever: no email, no name, no code string, no
   cart contents. Handles and section keys only.

   Cost: one IntersectionObserver for the sections, delegated click
   listeners on document, one submit listener, and MutationObservers
   on two attributes (the popup's class, the bag totals' aria-busy).
   No polling, no scroll handlers, no layout reads on scroll.

   Section type: Shopify does not expose a section's type to the
   storefront, so `section` is the template's key for it (for example
   ps_ugc_rail_squadron, which also tells the two UGC rails apart) and
   `section_type` is the class of the section's root element (for
   example ps-ugcr), which is the same for every copy of that section.
   ============================================================ */
(function () {
  'use strict';

  var BUILD = 'ps-v4-g5-gtag';
  window.psEvents = { build: BUILD };

  var main = document.getElementById('MainContent');
  var TEMPLATE = (main && main.getAttribute('data-template')) || '';
  var KIND = TEMPLATE.split('.')[0];

  var GA4_ID = 'G-QRVPB4P0BW';
  var queue = [];
  var timers = false;

  function allowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }
  /* GA4: only through the channel's tag, only with analytics consent. */
  function flush() {
    try {
      if (!queue.length || typeof window.gtag !== 'function' || !allowed()) return;
      var q = queue; queue = [];
      q.forEach(function (ev) {
        var p = { send_to: GA4_ID };
        for (var k in ev[1]) if (Object.prototype.hasOwnProperty.call(ev[1], k)) p[k] = ev[1][k];
        window.gtag('event', ev[0], p);
      });
    } catch (e) { /* an analytics failure must never break the page */ }
  }
  function later() {
    [1500, 4000, 10000].forEach(function (ms) { setTimeout(flush, ms); });
  }
  function publish(name, data) {
    data = data || {};
    data.template = TEMPLATE;
    try {
      var a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') a.publish(name, data);
    } catch (e) { /* an analytics failure must never break the page */ }
    if (queue.length < 40) queue.push([name, data]);
    flush();
    if (queue.length && !timers) { timers = true; later(); }
  }
  /* The tag arrives after the banner is accepted: try again then. */
  document.addEventListener('visitorConsentCollected', function () { later(); });
  window.addEventListener('pagehide', flush);

  /* "template--26318__ps_ugc_rail_squadron" -> "ps_ugc_rail_squadron" */
  function sectionKey(el) {
    var s = el && el.closest ? el.closest('.shopify-section') : null;
    if (!s || !s.id) return '';
    var id = s.id.replace(/^shopify-section-/, '');
    var i = id.lastIndexOf('__');
    return i >= 0 ? id.slice(i + 2) : id;
  }
  function sectionType(s) {
    for (var c = s.firstElementChild; c; c = c.nextElementSibling) {
      var t = c.tagName;
      if (t === 'STYLE' || t === 'SCRIPT' || t === 'LINK' || t === 'TEMPLATE' || t === 'NOSCRIPT') continue;
      /* skip an unclassed anchor such as <span id="ps-category-tiles"> */
      var cls = (c.getAttribute('class') || '').trim().split(/\s+/)[0];
      if (cls) return cls;
    }
    return '';
  }
  function handleFrom(href) {
    var m = /\/products\/([^/?#]+)/.exec(href || '');
    return m ? decodeURIComponent(m[1]) : '';
  }
  function listOf(el) {
    var type = 'other';
    if (el.closest('.ps-coll-grid__item')) type = 'grid';
    else if (el.closest('.ps-prow__item, .ps-cart-page__upsell-item')) type = 'rail';
    return { list: sectionKey(el), list_type: type };
  }
  function safe(fn) {
    return function (e) { try { fn(e); } catch (err) { /* never throw from an analytics hook */ } };
  }

  /* ── ps_section_view ───────────────────────────────────────── */
  function sections() {
    if (!main || (KIND !== 'index' && KIND !== 'product') || !('IntersectionObserver' in window)) return;
    var list = [].filter.call(main.children, function (c) { return c.classList && c.classList.contains('shopify-section'); });
    if (!list.length) return;
    var pos = new Map();
    list.forEach(function (s, i) { pos.set(s, i + 1); });
    var steps = [];
    for (var t = 0; t <= 20; t++) steps.push(t / 20);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var h = en.boundingClientRect.height;
        if (h < 1) return;
        var vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight;
        /* Half on screen: half of the section is visible, or (a section taller
           than two screens) it fills half the screen. */
        if (en.intersectionRatio >= 0.5 || en.intersectionRect.height >= vh * 0.5) {
          io.unobserve(en.target);
          publish('ps_section_view', {
            section: sectionKey(en.target) || en.target.id,
            section_type: sectionType(en.target),
            position: pos.get(en.target) || 0
          });
        }
      });
    }, { threshold: steps });
    list.forEach(function (s) { io.observe(s); });
  }

  /* ── taps: one delegated listener (bubble phase, so a rail that
     swallows a drag's click is not counted as a tap) ──────────── */
  document.addEventListener('click', safe(function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el;

    if ((el = t.closest('.psv3-hero__btn, .psv3-hero__bundle'))) {
      var which = el.classList.contains('psv3-hero__bundle')
        ? 'bundle_link'
        : (el.textContent || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      var href = '';
      try { href = new URL(el.href, location.href).pathname; } catch (x) {}
      publish('ps_hero_cta', { which: which, href: href });
      return;
    }

    if ((el = t.closest('[data-ps-qadd-btn], .ps-qadd__choose'))) {
      var card = el.closest('.ps-pcard');
      var link = card && card.querySelector('.ps-pcard__link');
      var q = listOf(el);
      publish('ps_quick_add', {
        handle: handleFrom(link ? link.getAttribute('href') : el.getAttribute('href')),
        kind: el.hasAttribute('data-ps-qadd-btn') ? 'add' : 'choose',
        list: q.list, list_type: q.list_type
      });
      return;
    }

    if ((el = t.closest('.ps-pcard__link'))) {
      var l = listOf(el);
      publish('ps_card_click', { handle: handleFrom(el.getAttribute('href')), list: l.list, list_type: l.list_type });
      return;
    }

    if ((el = t.closest('.ps-satc__btn'))) {
      publish('ps_sticky_atc', { handle: handleFrom(location.pathname) });
      return;
    }

    if ((el = t.closest('.ps-ugcr__tile')) && !el.classList.contains('ps-ugcr__tile--ph')) {
      var sec = el.closest('.ps-ugcr');
      var dlg = sec && sec.querySelector('dialog.ps-ugcr__viewer');
      var shop = dlg && dlg.querySelector('[data-ugcr-shop]');
      publish('ps_ugc_open', {
        position: (parseInt(el.getAttribute('data-i'), 10) || 0) + 1,
        has_product: !!(shop && !shop.hidden && shop.getAttribute('href')),
        rail: sectionKey(el)
      });
    }
  }));

  /* ── ps_popup ──────────────────────────────────────────────── */
  function popup() {
    var root = document.getElementById('ps-popup');
    if (!root || !('MutationObserver' in window)) return;
    var returning = /[?&]customer_posted=true/.test(location.search);
    var open = root.classList.contains('is-open');
    if (open && !returning) publish('ps_popup', { action: 'shown' });
    new MutationObserver(safe(function () {
      var now = root.classList.contains('is-open');
      if (now === open) return;
      open = now;
      if (now && !returning) publish('ps_popup', { action: 'shown' });
      else if (!now) publish('ps_popup', { action: 'closed' });
    })).observe(root, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('submit', safe(function (e) {
      if (e.target && e.target.id === 'ps-popup-signup-form') publish('ps_popup', { action: 'submitted' });
    }), true);
  }

  /* ── ps_code_applied: the bag's code field (snippets/ps-bag-totals,
     wired in snippets/ps-cart-drawer). While a code is checked the
     totals block carries aria-busy="true"; when that clears, the field
     says what happened: emptied and closed = applied, the message
     shown and the input marked invalid = rejected. The code itself is
     never read. ──────────────────────────────────────────────── */
  function codes() {
    if (!('MutationObserver' in window)) return;
    [].forEach.call(document.querySelectorAll('[data-ps-bagtot]'), function (box) {
      new MutationObserver(safe(function (muts) {
        var was = muts.some(function (m) { return m.oldValue === 'true'; });
        if (!was || box.getAttribute('aria-busy') === 'true') return;
        var msg = box.querySelector('[data-bt-msg]');
        var input = box.querySelector('[data-bt-input]');
        var where = box.closest('#ps-cart-drawer') ? 'drawer' : 'bag_page';
        if (msg && !msg.hidden && input && input.getAttribute('aria-invalid') === 'true') {
          publish('ps_code_applied', { result: 'rejected', where: where });
        } else if (input && input.value === '') {
          publish('ps_code_applied', { result: 'applied', where: where });
        }
      })).observe(box, { attributes: true, attributeFilter: ['aria-busy'], attributeOldValue: true });
    });
  }

  function start() {
    try { sections(); } catch (e) {}
    try { popup(); } catch (e) {}
    try { codes(); } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
